import rvApi from "@/rvapi";
import { defineStore } from "pinia";
import type {
  StickerCollection,
  StickerCreateRequest,
  StickerCreateCollectionReqestParam,
  StickerUpload,
  CreateCollectionSubmitStatus,
  StickerNotification,
  Sticker,
  StickerUpdateParams,
  StickerCollectionTag,
} from "@/types/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";
import constant from "@/constant";

const getInitialCreateCollectionRequest =
  (): StickerCreateCollectionReqestParam => ({
    svName: "",
    enName: "",
    active: false,
    categories: [],
    svTags: [],
    enTags: [],
    stickers: [],
    languages: [],
    visualQualitySelections: {},
    themeSelections: {},
  });

const getInitialCreateCollectionSubmitStatus =
  (): CreateCollectionSubmitStatus => ({
    step1: false,
    step2: false,
    step3: false,
    step4: false,
    step5: false,
  });

const getInitialNotificationObj = (): StickerNotification => ({
  show: false,
  status: "",
  message: "",
  heading: "",
});

const getInitialCurrentCollectionObj = (): StickerCollection => ({
  collectionId: 0,
  enname: "",
  svname: "",
  stickerCount: 0,
  tags: [],
  description: "",
  thumbnails: [],
  updatedBy: "string",
  active: false,
  completed: false,
  languages: [],
  categories: [],
});

const DEFAULT_STICKER_LANGUAGES = ["swedish", "english"];

export const userStickers = defineStore("stickers", {
  state: () => ({
    stickerCollections: [] as StickerCollection[],
    userSelectedCategories: [] as number[],
    createCollectionReqestParams:
      getInitialCreateCollectionRequest() as StickerCreateCollectionReqestParam,
    createCollectionProgress:
      getInitialCreateCollectionSubmitStatus() as CreateCollectionSubmitStatus,
    notificationObj: getInitialNotificationObj() as StickerNotification,
    currentCollection: getInitialCurrentCollectionObj() as StickerCollection,
    currentCollectionStickers: [] as Sticker[],
  }),
  actions: {
    reset() {
      this.createCollectionReqestParams = getInitialCreateCollectionRequest();
      this.createCollectionProgress = getInitialCreateCollectionSubmitStatus();
      this.currentCollection = getInitialCurrentCollectionObj();
    },
    resetNotification() {
      this.notificationObj.show = false;
      this.notificationObj.status = "";
      this.notificationObj.message = "";
      this.notificationObj.heading = "";
    },
    async getAllStickerCollections() {
      try {
        this.stickerCollections = await rvApi.getAllStickerCollections();
      } catch {
        console.log("failed to get stickers");
      }
    },
    setUserCategories(categories: number[]) {
      this.createCollectionReqestParams.categories = categories;
    },
    setEnglishName(name: string) {
      this.createCollectionReqestParams.enName = name;
    },
    setVisualQualitySelections(selections: Record<string, string[]>) {
      this.createCollectionReqestParams.visualQualitySelections = selections;
    },
    setThemeSelections(selections: Record<string, string[]>) {
      this.createCollectionReqestParams.themeSelections = selections;
    },
    syncCategoriesFromSelections() {
      const visualIds = Object.values(
        this.createCollectionReqestParams.visualQualitySelections ?? {},
      ).flat();
      const themeIds = Object.values(
        this.createCollectionReqestParams.themeSelections ?? {},
      ).flat();
      this.createCollectionReqestParams.categories = [
        ...new Set([...visualIds, ...themeIds]),
      ].map(Number);
    },
    populateCreateCollectionRequest(
      param: string | number[] | string[] | StickerUpload[],
      paramType: string,
    ) {
      switch (paramType) {
        case "category":
          this.createCollectionReqestParams.categories = param as number[];
          break;
        case "english_name":
          this.createCollectionReqestParams.enName = param as string;
          break;
        case "swedish_name":
          this.createCollectionReqestParams.svName = param as string;
          break;
        case "svTags":
          this.createCollectionReqestParams.svTags = param as string[];
          break;
        case "enTags":
          this.createCollectionReqestParams.enTags = param as string[];
          break;
        case "stickers":
          this.createCollectionReqestParams.stickers = param as StickerUpload[];
          break;
        default:
          break;
      }
    },
    populateNotificationObj({
      show,
      status,
      message,
      heading,
    }: StickerNotification) {
      this.notificationObj.show = show;
      this.notificationObj.status = status;
      this.notificationObj.message = message;
      this.notificationObj.heading = heading ?? "";
    },

    populateCreateCollectionRequestFromCollection(
      collection: StickerCollection,
    ) {
      const englishTags =
        collection.tags.find(
          (tagObj: StickerCollectionTag) => tagObj.language === "english",
        )?.tags ?? [];
      const swedishTags =
        collection.tags.find(
          (tagObj: StickerCollectionTag) => tagObj.language === "swedish",
        )?.tags ?? [];

      const tagCategoryIds = collection.tagCategoryIds ?? [];
      const tagIdSet = new Set(tagCategoryIds);

      const tagManagementStore = useTagManagementStore();
      const visualGroups = tagManagementStore.getThemeCategories(
        constant.TAG_THEME_IDS.VISUAL_QUALITY,
      );
      const themeGroups = tagManagementStore.getThemeCategories(
        constant.TAG_THEME_IDS.THEME,
      );

      const visualQualitySelections: Record<string, string[]> = {};
      for (const group of visualGroups) {
        const matchedIds = (group.children ?? [])
          .filter((child) => tagIdSet.has(child.categoryId))
          .map((child) => String(child.categoryId));
        if (matchedIds.length > 0) {
          visualQualitySelections[group.categoryId] = matchedIds;
        }
      }

      const themeSelections: Record<string, string[]> = {};
      for (const group of themeGroups) {
        const matchedIds: string[] = [];
        for (const child of group.children ?? []) {
          if (tagIdSet.has(child.categoryId)) {
            matchedIds.push(String(child.categoryId));
          }
          for (const grandchild of child.children ?? []) {
            if (tagIdSet.has(grandchild.categoryId)) {
              matchedIds.push(String(grandchild.categoryId));
            }
          }
        }
        if (matchedIds.length > 0) {
          themeSelections[group.categoryId] = matchedIds;
        }
      }

      this.createCollectionReqestParams = {
        svName: collection.svname,
        enName: collection.enname,
        active: collection.active,
        categories: [...tagCategoryIds],
        svTags: [...swedishTags],
        enTags: [...englishTags],
        stickers: [],
        languages: [...(collection.languages ?? ["swedish", "english"])],
        visualQualitySelections,
        themeSelections,
      };
    },

    async createStickerCollection() {
      const requst = {
        svname: this.createCollectionReqestParams.svName,
        enname: this.createCollectionReqestParams.enName,
        active: this.createCollectionReqestParams.active,
        categories: this.createCollectionReqestParams.categories,
        languages: ["swedish", "english"],
        colleDesc: [
          {
            language: "swedish",
            description: this.createCollectionReqestParams.svName,
          },
          {
            language: "english",
            description: this.createCollectionReqestParams.enName,
          },
        ],
        tags: [
          {
            language: "swedish",
            tags: this.createCollectionReqestParams.svTags,
          },
          {
            language: "english",
            tags: this.createCollectionReqestParams.enTags,
          },
        ],
        stickers: this.createCollectionReqestParams.stickers,
      };
      await rvApi.createStickerCollection(requst);
    },

    async updateStickerCollection() {
      const request = {
        svname: this.createCollectionReqestParams.svName,
        enname: this.createCollectionReqestParams.enName,
        active: this.createCollectionReqestParams.active,
        categories: this.createCollectionReqestParams.categories,
        languages: this.createCollectionReqestParams.languages ?? [
          "swedish",
          "english",
        ],
        colleDesc: [
          {
            language: "swedish",
            description: this.createCollectionReqestParams.svName,
          },
          {
            language: "english",
            description: this.createCollectionReqestParams.enName,
          },
        ],
        tags: [
          {
            language: "swedish",
            tags: this.createCollectionReqestParams.svTags,
          },
          {
            language: "english",
            tags: this.createCollectionReqestParams.enTags,
          },
        ],
      };
      await rvApi.updateStickerCollection(
        this.currentCollection.collectionId,
        request,
      );
    },

    async getStickerCollectionById(collectionId: number) {
      this.currentCollection =
        await rvApi.getStickerCOllectionById(collectionId);
    },
    async getStickersForCollection(collectionId: number) {
      this.currentCollectionStickers =
        await rvApi.getStickersForCollection(collectionId);
    },

    async updateSticker(requestParam: StickerUpdateParams) {
      const request = {
        filename: "dummy.svg",
        completed: requestParam.completed,
        collectionId: requestParam.collectionId,
        categories: requestParam.categories,
        languages: requestParam.languages,
        stickerDesc: [
          {
            language: "swedish",
            description: requestParam.swedishAltText,
          },
          {
            language: "english",
            description: requestParam.englishAltText,
          },
        ],
        tags: [
          {
            language: "swedish",
            tags: requestParam.swedishTags,
          },
          {
            language: "english",
            tags: requestParam.englishTags,
          },
        ],
      };
      await rvApi.updateSticker(requestParam.stickerId, request);
    },

    async appendStickersToCollection(
      collectionId: number,
      stickers: StickerUpload[],
    ) {
      const categories = [...(this.currentCollection.categories ?? [])];
      const requests: StickerCreateRequest[] = stickers.map((sticker) => ({
        filename: sticker.filename,
        collectionId,
        base64: sticker.base64,
        hidden: false,
        categories,
        languages: [...DEFAULT_STICKER_LANGUAGES],
      }));

      await Promise.all(
        requests.map((request) => rvApi.createSticker(request)),
      );
    },

    deleteStickerCollection(collectionId: number) {
      return rvApi.deleteStickerCollection(collectionId);
    },

    deleteSticker(stickerId: number) {
      return rvApi.deleteSticker(stickerId);
    },
  },
  getters: {
    getOrderedStickerCOllection: (state) => {
      const orderedCollection = state.stickerCollections.sort(
        (collection1, collection2) => {
          return collection2.collectionId - collection1.collectionId;
        },
      );
      return orderedCollection;
    },
    getOrderedCollectionStickers: (state) => {
      return [...state.currentCollectionStickers].sort(
        (a, b) => a.stickerId - b.stickerId,
      );
    },
  },
});
